<template lang="pug">
.page-content
  .benjamin-transcript-view
    div
      #osdToolbar.field.has-addons(ref="osdToolbar")
        #personsToggle.control
          .dropdown(:class="{'is-active': displayPersonAreas}")
            .dropdown-trigger
              .button(:class="{'is-active': displayPersonAreas}" :title="$t('transcript.markPersons')" @click="togglePersonAreas")
                i.fa.fa-fw.fa-male
            person-list
        #projectsToggle.control(@click="toggleProjectAreas")
          .button(data-view="view-projects" :title="$t('transcript.markProjects')")
            i.fa.fa-fw.fa-file-text-o
        #osdZoomIn.control(@click="zoomIn")
          .button(:title="$t('transcript.scaleUp')")
            i.fa.fa-fw.fa-search-plus
        #osdZoomOut.control(@click="zoomOut")
          .button(:title="$t('transcript.scaleDown')")
            i.fa.fa-fw.fa-search-minus
        #osdHome.control
          .button(:title="$t('transcript.reset')")
            i.fa.fa-fw.fa-search
        #osdFullScreen.control(@click="toggleFullScreen")
          .button(:title="$t('transcript.fullScreenMode')")
            i.fa.fa-fw.fa-arrows-alt
        #osdRotateLeft.control
          .button(:title="$t('transcript.turnLeft')")
            i.fa.fa-fw.fa-undo
        #osdRotateRight.control
          .button(:title="$t('transcript.turnRight')")
            i.fa.fa-fw.fa-repeat
      .field
        #opacitySlider.control(:title="$t('transcript.crossFade')")
          input#opacitySliderValue.slider.is-fullwidth(type="range" step="1" min="0" max="100" :value="opacity")
  .benjamin-transcript-navigator
    .field.has-addons
      #watchlist.control
        .dropdown.is-right(:class="{'is-active': displayWatchlist}")
          .dropdown-trigger
            button.button(:class="{'is-active': displayWatchlist}" :title="$t('transcript.watchlist')" @click="toggleWatchlist")
              i.fa.fa-fw.fa-bookmark
          watchlist
      #metadata.control
        .dropdown.is-right(:class="{'is-active': displayMetadata}")
          .dropdown-trigger
            button.button(:class="{'is-active': displayMetadata}" :title="$t('transcript.metadata')" @click="toggleMetadataSnippet")
              i.fa.fa-fw.fa-sticky-note
          metadata-snippet
      #citation.control
        .dropdown.is-right(:class="{'is-active': displayCitation}")
          .dropdown-trigger
            button.button(:class="{'is-active': displayCitation}" :title="$t('transcript.citation')" @click="toggleCitation")
              i.fa.fa-fw.fa-quote-right
          citation
      .control
        a.button(:href="teiDownloadLink" :title="$t('transcript.downloadTei')" download)
          i.fa.fa-fw.fa-file-excel-o
      .control
        .button(:title="$t('transcript.toc')" class="transcript-modal-button" @click="handleModalButtonClick('modal-contents', $event)")
          i.fa.fa-fw.fa-list
      .control
        .button(:title="$t('transcript.bundle')" class="transcript-modal-button" @click="handleModalButtonClick('modal-convolute', $event)")
          i.fa.fa-fw.fa-picture-o
    #osdNavigator.overview
  .benjamin-transcript-nav
      .buttons.previous
        router-link.button(:disabled="!prevPage" :to="prevPage ? prevPage.route : {}")
          i.fa.fa-angle-left
          span.text(v-if="prevPage") {{ prevPage.title }}: {{ prevPage.sig }}
      .buttons.next
        router-link.button(:disabled="!nextPage" :to="nextPage ? nextPage.route : {}")
          span.text(v-if="nextPage") {{ nextPage.title }}: {{ nextPage.sig }}
          i.fa.fa-angle-right
  .benjamin-transcript
    .benjamin-facsimile
      project-area-overlay(:transcriptHook="transcriptHook" :svgNode="svgOverlayNode" :svgRoot="svgOverlayRoot" :textSvg="transcriptLinesSvg")
      single-line-transcript(:svgNode="svgOverlayNode" :transcriptHook="transcriptHook")
      transcript-text-layer(:svgNode="svgOverlayRoot" :transcriptHook="transcriptHook" :textSvg="transcriptLinesSvg")
      milkglass-layer(:milkglassHook="milkglassHook")
    transcript-breadcrumb
</template>

<script>
// This is the main component for managing the display of single page facsimiles and corresponding transcriptions.
// Child components or other modules may want to react to the replacement of the SVG content of this component. The
// data property "eventBus" can be injected into such child components. A custom event "svg-change" is emitted on this bus
// and allows listening modules to react to such changes.

// required node modules
const throttle = require("lodash.throttle");
const OpenSeadragon = require("openseadragon");
const { mapGetters, mapMutations, mapState } = require("vuex");

// required lib modules
const { SvgConstants } = require("../config/config");
const { messages } = require("../i18n/transcript");
// eslint-disable-next-line
const osdSvg = require("../openseadragon-svg-overlay.js");
const toc = require("../helpers/toc");
const domParser = new DOMParser();

// used components
import Citation from "../components/Citation";
import MetadataSnippet from "../components/MetadataSnippet";
import PersonList from "../components/PersonList";
import ProjectAreaOverlay from "../components/transcript/ProjectAreaOverlay";
import MilkglassLayer from "../components/transcript/MilkglassLayer";
import SingleLineTranscript from "../components/transcript/SingleLineTranscript";
import TranscriptTextLayer from "../components/transcript/TranscriptTextLayer";
import TranscriptBreadcrumb from "../components/transcript/TranscriptBreadcrumb";
import Watchlist from "../components/Watchlist";

// other constants
const { assign } = Object;
const isIE = !!document.documentMode;
const prefixUrl = "/openseadragon/images/";
const { Rect, Point } = OpenSeadragon;

export default {
    name: "Transcript",

    components: {
        Citation,
        MetadataSnippet,
        PersonList,
        ProjectAreaOverlay,
        MilkglassLayer,
        SingleLineTranscript,
        TranscriptTextLayer,
        TranscriptBreadcrumb,
        Watchlist,
    },

    i18n: { messages },

    computed: {
        teiDownloadLink() {
            return `/pages/${this.currentPage.id}.xml`;
        },

        ...mapState("transcript", [
            "eventBus",
            "opacity",
            "displayCitation",
            "displayMetadata",
            "displayPersonAreas",
            "displayProjectAreas",
            "displayWatchlist",
        ]),

        ...mapGetters("transcript", ["facsimile"]),

        ...mapGetters("toc", {
            currentPage: "current",
            nextPage: "next",
            prevPage: "prev",
        }),
    },

    data() {
        return {
            transcriptLinesSvg: () => {
                if (!this.currentPage || !this.currentPage.id) {
                    console.log("UNDEF PAGE ID!");
                    return;
                }
                return svg(this.currentPage.id);
            },
            svgOverlayRoot: () => {
                if (!this.osd || !this.osd.svgOverlay()) {
                    return;
                }
                const svgOverlayNode = this.osd.svgOverlay().node();
                return svgOverlayNode.ownerSVGElement;
            },
            svgOverlayNode: () => {
                if (!this.osd || !this.osd.svgOverlay()) {
                    return;
                }
                return this.osd.svgOverlay().node();
            },
            transcriptHook: () => {
                if (!this.transcript) {
                    return;
                }

                return this.transcript;
            },
            milkglassHook: () => {
                if (!this.milkglass) {
                    return;
                }
                return this.milkglass;
            },
        };
    },

    watch: {
        facsimile() {
            this.openFacsimile();
        },

        opacity() {
            this.setOpacity();
        },
    },

    methods: assign(
        {
            handleModalButtonClick(type, evt) {
                evt.preventDefault();
                const button = evt.target;
                const modal = document.getElementById(type);

                [button, modal].forEach((el) =>
                    el?.classList?.add("is-active")
                );
                document.documentElement.classList.add("is-clipped");

                if (type == "modal-contents") {
                    scrollToCurrentTocEntry(this);
                }
            },

            mouseOver() {},

            mouseOut() {},

            openFacsimile() {
                const { osd, facsimile } = this;
                if (!osd) {
                    return;
                }
                //console.log("opening facsimile " + facsimile)

                this.facsimileOpen = false;
                osd.close();

                if (!facsimile) {
                    return;
                }

                osd.addTiledImage({
                    tileSource: facsimile,
                    success: () => {
                        this.osd.viewport.fitBoundsWithConstraints(
                            new Rect(0, 0, 1, 1),
                            true
                        );
                        this.setProjection();
                        this.setOpacity();
                        this.facsimileOpen = true;
                        if (this.osd) {
                            this.eventBus.$emit("facsimile-open");
                        }
                    },
                });
            },

            toggleFullScreen() {
                if (OpenSeadragon.isFullScreen()) {
                    OpenSeadragon.exitFullScreen();
                } else {
                    OpenSeadragon.requestFullScreen(
                        document.getElementById("full-screen-content")
                    );
                }
            },

            withOpenFacsimile(fn) {
                const { osd, facsimileOpen } = this;
                return osd ? fn(osd, facsimileOpen) : false;
            },

            zoomIn() {
                this.withOpenFacsimile(({ viewport }) => viewport.zoomBy(2));
            },

            zoomOut() {
                this.withOpenFacsimile(({ viewport }) => viewport.zoomBy(0.5));
            },

            rotate(degrees) {
                this.withOpenFacsimile(({ viewport }) =>
                    viewport.setRotation(viewport.getRotation() + degrees)
                );
            },

            rotateLeft() {
                this.rotate(-90);
            },

            rotateRight() {
                this.rotate(90);
            },

            svg(svg) {
                this.withOpenFacsimile(() => {
                    const { transcript } = this;
                    Array.from(transcript.childNodes)
                        .filter(
                            (node) => typeof node.getAttribute === "function"
                        )
                        .filter(
                            (node) =>
                                !node
                                    .getAttribute("class")
                                    .includes("milkglass")
                        )
                        .forEach((node) => node.parentNode.removeChild(node));

                    if (!svg) {
                        return;
                    }

                    this.eventBus.$emit("svg-change", svg);
                });
            },

            setOpacity() {
                this.eventBus.$emit("overlay-opacity-change", {});
            },

            setProjection() {
                this.withOpenFacsimile((osd) => {
                    const { container, viewport, world } = osd;
                    const lastDim = (this.containerDimensions =
                        this.containerDimensions || new Point(0, 0));

                    const currentDim = new Point(
                        container.clientWidth,
                        container.clientHeight
                    );

                    if (!lastDim.equals(currentDim)) {
                        this.transcriptDoc.setAttribute("width", currentDim.x);
                        this.transcriptDoc.setAttribute("height", currentDim.y);
                        this.containerDimensions = currentDim;
                    }

                    const image = world.getItemAt(0),
                        { x, y } = viewport.pixelFromPoint(
                            new Point(0, 0),
                            true
                        ),
                        scale = image.viewportToImageZoom(
                            viewport.getZoom(true)
                        ),
                        rotation = viewport.getRotation();

                    setScaling(this.transcript, x, y, scale, rotation);

                    [image, world, viewport].forEach((c) => c.update());
                    osd.svgOverlay().resize();
                    const pb = container.querySelector(".projectareas");
                    setScaling(pb, x, y, scale, rotation);
                });
            },

            toggleCitation() {
                this.toggleCitationDisplay();
            },

            togglePersonAreas() {
                this.togglePersonAreasDisplay();
            },

            toggleProjectAreas() {
                this.toggleProjectAreasDisplay();
            },

            toggleWatchlist() {
                this.toggleWatchlistDisplay();
            },
        },
        mapMutations("transcript", [
            "changeOpacity",
            "toggleCitationDisplay",
            "togglePersonAreasDisplay",
            "toggleProjectAreasDisplay",
            "toggleMetadataSnippet",
            "toggleWatchlistDisplay",
        ])
    ),

    mounted() {
        document.documentElement.classList.add("has-navbar-fixed-bottom");

        const opacitySlider = this.$el.querySelector("#opacitySlider input");
        opacitySlider.addEventListener("input", (evt) => {
            this.changeOpacity({ opacity: evt.target.value });
        });
        if (isIE) {
            opacitySlider.addEventListener("change", (evt) => {
                this.changeOpacity({ opacity: evt.target.value });
            });
        }

        const element = this.$el.querySelector(".benjamin-facsimile");
        const osd = (this.osd = OpenSeadragon({
            prefixUrl,
            element,

            homeButton: "osdHome",
            rotateLeftButton: "osdRotateLeft",
            rotateRightButton: "osdRotateRight",

            showNavigator: true,
            navigatorId: "osdNavigator",
            showNavigationControl: true,
            showRotationControl: true,
            debugMode: false,
            zoomPerClick: 1,
        }));

        const transcriptDoc = (this.transcriptDoc = this.osd.canvas.appendChild(
            svgEl("svg")
        ));
        transcriptDoc.setAttribute("version", "1.1");
        transcriptDoc.setAttribute("xmlns", SvgConstants.NS);
        transcriptDoc.setAttribute("class", "transcript");

        assign(transcriptDoc.style, {
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
        });

        const transcript = (this.transcript = transcriptDoc.appendChild(
            svgEl("g")
        ));
        transcript.addEventListener("mouseover", this.mouseOver.bind(this));
        transcript.addEventListener("mouseout", this.mouseOut.bind(this));

        // add milkglass dummy node
        this.milkglass = transcript.appendChild(svgEl("rect"));
        this.eventBus.$emit("milkglass-create", {});

        const throttledProjectionUpdate = throttle(
            this.setProjection.bind(this),
            100
        );
        [
            "animation",
            "pan",
            "rotate",
            "resize",
            "zoom",
            "viewport-change",
        ].forEach((evt) => {
            osd.addHandler(evt, throttledProjectionUpdate);
        });

        this.openFacsimile();
    },

    beforeDestroy() {
        this.osd.destroy();
        document.documentElement.classList.remove("has-navbar-fixed-bottom");
        this.transcript = undefined;

        this.$el
            .querySelector("#osdToolbar #osdZoomIn")
            .removeEventListener("click", this.zoomIn);
        this.$el
            .querySelector("#osdToolbar #osdZoomOut")
            .removeEventListener("click", this.zoomOut);
    },

    beforeRouteEnter(to, from, next) {
        const { id } = toc.fromRoute(to);
        return svg(id).then((svg) => next((vm) => vm.svg(svg)));
    },

    beforeRouteUpdate(to, from, next) {
        const { id } = toc.fromRoute(to);
        this.osd.svgOverlay().destroy();
        return svg(id)
            .then((svg) => this.svg(svg))
            .then(() => next());
    },
};

function svg(id) {
    //console.log("fetching input: " + `/pages/${id}.svg`)
    return fetch(`/pages/${id}.svg`)
        .then(
            (svg) => svg.blob(),
            () => undefined
        )
        .then(
            (svgBlob) =>
                svgBlob?.text() ??
                `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1"/>`
        )
        .then((svgText) => domParser.parseFromString(svgText, "image/svg+xml"));
}

function svgEl(name) {
    return document.createElementNS(SvgConstants.NS, name);
}

const scrollToCurrentTocEntry = function (vueComponent) {
    vueComponent.$nextTick(() => {
        if (document.getElementById("currentTocItem")) {
            vueComponent.$scrollTo("#currentTocItem", 800, {
                container: ".modal-content",
            });
        }
    });
};

function setScaling(element, x, y, scale, rotation) {
    if (element) {
        element.setAttribute(
            "transform",
            `translate(${Math.floor(x)}, ${Math.floor(
                y
            )}) scale(${scale}) rotate(${rotation})`
        );
    }
}
</script>

<style>
.page-content:not(:has(text)) svg.transcript {
    opacity: 0;
}

.page-content:not(:has(text)) .field:has(#opacitySlider) {
    visibility: hidden;
    pointer-events: none;
}
</style>
